import { createApp } from 'vue'
// @ts-expect-error: no types available
import VueConfetti from 'vue-confetti'
import VueDOMPurifyHTML from 'vue-dompurify-html'
import VueVirtualScroller from 'vue-virtual-scroller'
import { plugin as unleashPlugin } from '@unleash/proxy-client-vue'
import Notifications from '@kyvg/vue3-notification'
import { Integrations } from '@sentry/tracing'
import * as Sentry from '@sentry/vue'
import smoothscroll from 'smoothscroll-polyfill'
import config, { environment } from '@/config'
import piniaStore from '@/store/pinia'
import Config from '@/utils/config'
import hasAccessForTasksPlugin from '@/utils/helpers/hasAccessForTasks'
import isAppInstalled from '@/utils/helpers/isAppInstalled'
import isSameUser from '@/utils/helpers/isSameUser'
import PermissionsHelperPlugin from '@/utils/helpers/permissionsHelper'
import PlanFeatureLimitHelperPlugin from '@/utils/helpers/planFeatureLimitHelper'
import Sbnotify from '@/utils/helpers/sbnotify'
import i18n from '@/utils/helpers/translate'
import urlHelper from '@/utils/helpers/urlHelper'
import createUnleashClient, {
  unleashFlagPlugin,
} from '@/utils/setup-application/createUnleash'
import installEmitter from '@/utils/setup-application/emitter'
import installFilters from '@/utils/setup-application/filters'
import vueSafeHrefPlugin from '@/utils/setup-application/safeHref'
import installTranslateHelper from '@/utils/setup-application/translate-helper'
import BlokInk from '@storyblok/design-system'
import DiscardChangesModal from '@/components/ContentManager/DiscardChanges/DiscardChangesModal.vue'
import LabsContentPlugin from '@/components/ContentManager/LabsPage/labsContentPlugin'
import App from '@/App.vue'
import DiscardChangesPlugin from '@/plugins/discardChangesPlugin'
import router from '@/router'
import 'src/assets/styles/index.scss'

const app = createApp(App)

// router
app.use(router)
app.use(piniaStore)

// plugins
app.use(i18n)
app.use(BlokInk)
app.use(isAppInstalled)
app.use(isSameUser)
app.use(Config)
app.use(Notifications)
app.use(DiscardChangesPlugin)
app.use(Sbnotify)
app.use(urlHelper)
app.use(PermissionsHelperPlugin)
app.use(PlanFeatureLimitHelperPlugin)
app.use(hasAccessForTasksPlugin)
app.use(VueConfetti)
app.use(installEmitter)
app.use(installFilters)
app.use(vueSafeHrefPlugin)
app.use(installTranslateHelper)
app.use(VueVirtualScroller)
app.use(VueDOMPurifyHTML, {
  hooks: {
    afterSanitizeAttributes: (node: HTMLElement) => {
      if ('target' in node) {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer nofollow')
      }
    },
  },
})
app.use(LabsContentPlugin)

app.component('DiscardChangesModal', DiscardChangesModal)

// unleash client
const env = config?.data?.environment()
const unleashClient = createUnleashClient(env)
app.use(unleashPlugin, { unleashClient })
app.use(unleashFlagPlugin)

// init polyfill for smooth scrolling in safari
if (window && window.navigator) {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  if (isSafari) {
    smoothscroll.polyfill()
  }
}

if (window.Cypress) {
  window.app = app
}

router
  .isReady()
  .then(() => {
    const logToConsole = environment() === 'development'
    Sentry.init({
      app,
      dsn: 'https://9181e1dbfd9b4de9a0c903f98ba19026@o77796.ingest.sentry.io/5914554',
      environment: environment(),
      integrations: [
        new Integrations.BrowserTracing({
          routingInstrumentation: Sentry.vueRouterInstrumentation(router),
          tracingOrigins: ['app.storyblok.com', /^\//],
        }),
      ],
      release: process.env.SENTRY_RELEASE,
      tracesSampleRate: 0.01,
      // In local development, log the event to the console, rather than to Sentry
      beforeSend: (event) => {
        if (logToConsole) {
          console.error(event)
          // this drops the event and nothing will be send to sentry
          return null
        }
        return event
      },
      logErrors: logToConsole,
    })
  })
  .then(() => app.mount('#app'))

export default app
